import { useState, useEffect } from 'react';
import moment, { MomentInput } from 'moment-timezone';
import noop from 'utils/noop';
import { guessCurrentTimeZone } from 'utils/timeZone';
import { DAY } from 'utils/date';
import { Button, BUTTON_VARIANTS } from 'ui/button';
import { DateRangeCalendar } from './calendar/DateRangeCalendar';
import { ValidDateRange } from './calendar/types';
import { DatePickerInput } from './datePickerInput/DatePickerInput';
import { TimeInput } from './datePickerInput/TimeInput';
import { DateInput } from './datePickerInput/DateInput';
import { RelativeDateMenu } from './relativeDateMenu/RelativeDateMenu';
import { TimeZonePicker } from './timeZonePicker/TimeZonePicker';
import {
  RELATIVE_DATE_PRESETS,
  setDateWithMoment,
  CUSTOM_RANGE,
  setTimeZoneWithSameTime,
} from './helpers';
import { DateRangeChange, DateType, DateRangePickerProps } from './types';
import {
  DateRangePickerWrapper,
  InputBar,
  InputBarText,
  CalendarAndRelativeMenuWrapper,
  CalendarWrapper,
  Actions,
  TimeZonePickerWrapper,
} from './DateRangePicker.styles';
import { convertMomentToDateString, convertValidDateRange } from './momentToDateConversionHelpers';

export const DateRangePicker = ({
  areTimePickersDisabled = false,
  blockFutureDates = false,
  blockPastDates = false,
  cancelLabel = 'Cancel',
  customCalendarTileContent,
  dateRangePreset = '',
  defaultTimeZone = guessCurrentTimeZone(),
  endDate,
  hasActions = false,
  hasAllTimePreset = false,
  hideTimeInputs = false,
  hideTimeZonePicker = false,
  isTimeZonePickerDisabled = false,
  onCancel = noop,
  onDateRangeChange = noop,
  onPresetChange = noop,
  onSubmit = noop,
  onTimeZoneChange = noop,
  presets = RELATIVE_DATE_PRESETS,
  primaryActionLabel = 'Apply',
  promoteCustomInput = true,
  showRelativeDatePresets = true,
  startDate,
  timezoneHorizontalAlignment,
  useLocalTimeZone = true,
  validDateRange,
  ...props
}: DateRangePickerProps) => {
  const [start, setStart] = useState(startDate);
  const [end, setEnd] = useState(endDate);
  const [timeZone, setTimeZone] = useState(defaultTimeZone);
  const [isInvalidRange, setIsInvalidRange] = useState(false);
  const [updateCalDisplay, setUpdateCalDisplay] = useState(false);

  /** Used if showRelativeDatePresets is true */
  const [selectedPreset, setSelectedPreset] = useState(dateRangePreset);

  /** Sets the state values for the start ane end of the date range */
  const setRange = (newStartDate: DateType, newEndDate: DateType) => {
    setStart(newStartDate);
    setEnd(newEndDate);
  };

  const handleCustomDateChange = (newValue: DateRangeChange) => {
    const isInvalid = newValue?.startDate?.isAfter(newValue?.endDate as MomentInput) || false;
    setIsInvalidRange(isInvalid);

    // the calendar will return an empty endDate when the startDate is selected.
    // it will return both a startDate and endDate when the second date is selected.
    // we only want to fire the callback, when that is the case
    if (!isInvalid && newValue.startDate && newValue.endDate) {
      onDateRangeChange({
        startDate: setTimeZoneWithSameTime(newValue.startDate, timeZone),
        endDate: setTimeZoneWithSameTime(newValue.endDate, timeZone),
      });
    }
    setSelectedPreset(CUSTOM_RANGE);
    onPresetChange(CUSTOM_RANGE);
  };

  const handleNewCalendarDatesChange = (newValues: ValidDateRange) => {
    let newStartDate = setDateWithMoment(
      moment.tz(start || moment(), timeZone),
      moment(newValues.startDate),
    );

    let newEndDate = newValues.endDate
      ? setDateWithMoment(moment.tz(end || moment(), timeZone), moment(newValues.endDate))
      : null;

    if (hasAllTimePreset) {
      newStartDate = newStartDate.startOf(DAY);
      newEndDate = newEndDate?.endOf(DAY) || null;
    }

    setRange(newStartDate, newEndDate);
    handleCustomDateChange({
      startDate: newStartDate,
      endDate: newEndDate,
    });
  };

  const handlePresetChange = (preset: string, updatedTimeZone?: string) => {
    if (preset !== CUSTOM_RANGE) {
      const newRange = presets[preset].format(updatedTimeZone || timeZone);
      setRange(newRange.startDate, newRange.endDate);
      onDateRangeChange({ ...newRange });
      setUpdateCalDisplay(!updateCalDisplay); // rerender calendar, so that the current displayed month reflects the newest date range: ;
    }
    setSelectedPreset(preset);
    onPresetChange(preset);
  };

  const handleSetStart = (newStart: DateType) => {
    newStart?.startOf('minute');
    setStart(newStart);
    handleCustomDateChange({ startDate: newStart, endDate: end });
  };

  const handleSetEnd = (newEnd: DateType) => {
    newEnd?.endOf('minute');
    setEnd(newEnd);
    handleCustomDateChange({ startDate: start, endDate: newEnd });
  };

  const handleTimeZoneChange = (newTimeZone: string[]) => {
    const [timeZoneValue] = newTimeZone;
    setTimeZone(timeZoneValue);

    /** for custom inputs: change TZ only, and maintain original date & time */
    if (selectedPreset === CUSTOM_RANGE) {
      const startDateWithTZ = setTimeZoneWithSameTime(start, timeZoneValue);
      const endDateWithTZ = setTimeZoneWithSameTime(end, timeZoneValue);
      setRange(startDateWithTZ, endDateWithTZ);
      onDateRangeChange({ startDate: startDateWithTZ, endDate: endDateWithTZ });
    }

    /** for relative presets: convert date range values based on new TZ */
    if (showRelativeDatePresets && selectedPreset && selectedPreset !== CUSTOM_RANGE) {
      handlePresetChange(selectedPreset, timeZoneValue);
    }
    onTimeZoneChange(timeZoneValue);
  };

  useEffect(() => {
    /** Priotize preset as source of truth if it does not match with date values */
    if (dateRangePreset && dateRangePreset !== CUSTOM_RANGE) {
      const newRange = presets[dateRangePreset].format(timeZone);
      if (newRange.startDate !== start) {
        setStart(newRange.startDate);
      }
      if (newRange.endDate !== end) {
        setEnd(newRange.endDate);
      }
    }
    /** Check if time zone matches time zones of dates and correct with time zone as source of truth */
    if (start && moment.tz(timeZone).zoneAbbr() !== start.zoneAbbr()) {
      setStart(setTimeZoneWithSameTime(start, timeZone));
      setEnd(setTimeZoneWithSameTime(end, timeZone));
    }
  }, []);

  // keep start and end date in sync with props
  useEffect(() => {
    // set internal state based on prop inputs from parent component
    setRange(startDate, endDate);
  }, [startDate, endDate]);

  const showStartTime = (!hasAllTimePreset || !!start) && !hideTimeInputs;
  const showEndTime = (!hasAllTimePreset || !!end) && !hideTimeInputs;

  const CustomInputBar = () => (
    <InputBar isPromoted={promoteCustomInput}>
      <DatePickerInput>
        <DateInput
          date={useLocalTimeZone ? start : moment.tz(start, timeZone)}
          onDateChange={handleSetStart}
          id="start-date-input"
        />
        {showStartTime && (
          <TimeInput
            time={useLocalTimeZone ? start : moment.tz(start, timeZone)}
            onTimeChange={handleSetStart}
            isDisabled={areTimePickersDisabled}
            id="start-time-input"
          />
        )}
      </DatePickerInput>
      <InputBarText isPromoted={promoteCustomInput}>to</InputBarText>
      <DatePickerInput isInvalidRange={isInvalidRange}>
        <DateInput
          date={useLocalTimeZone ? end : moment.tz(end, timeZone)}
          isInvalidRange={isInvalidRange}
          onDateChange={handleSetEnd}
          id="end-date-input"
        />
        {showEndTime && (
          <TimeInput
            time={useLocalTimeZone ? end : moment.tz(end, timeZone)}
            isDisabled={areTimePickersDisabled}
            isInvalidRange={isInvalidRange}
            onTimeChange={handleSetEnd}
            id="end-time-input"
          />
        )}
      </DatePickerInput>
      {!hideTimeZonePicker && (
        <TimeZonePickerWrapper>
          <TimeZonePicker
            isDisabled={isTimeZonePickerDisabled}
            onTimeZoneChange={handleTimeZoneChange}
            timeZone={timeZone}
            horizontalAlignment={timezoneHorizontalAlignment}
          />
        </TimeZonePickerWrapper>
      )}
    </InputBar>
  );

  return (
    <DateRangePickerWrapper {...props}>
      {promoteCustomInput && <CustomInputBar />}
      <CalendarAndRelativeMenuWrapper>
        {showRelativeDatePresets && (
          <RelativeDateMenu
            selectedPreset={selectedPreset}
            onPresetChange={handlePresetChange}
            presets={presets}
          />
        )}
        <CalendarWrapper>
          <DateRangeCalendar
            blockFutureDates={blockFutureDates}
            blockPastDates={blockPastDates}
            tileContent={customCalendarTileContent}
            endDate={convertMomentToDateString(end, !useLocalTimeZone ? timeZone : null)}
            key={updateCalDisplay.toString()} // rerenders the calendar so that the displayed month matches the preset
            onDatesChange={handleNewCalendarDatesChange}
            startDate={convertMomentToDateString(start, !useLocalTimeZone ? timeZone : null)}
            validDateRange={convertValidDateRange(validDateRange)}
            {...props}
          />
        </CalendarWrapper>
      </CalendarAndRelativeMenuWrapper>
      {!promoteCustomInput && !hasActions && <CustomInputBar />}
      {!!hasActions && (
        <Actions>
          <Button variant={BUTTON_VARIANTS.SECONDARY} onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button variant={BUTTON_VARIANTS.PRIMARY} onClick={onSubmit}>
            {primaryActionLabel}
          </Button>
        </Actions>
      )}
    </DateRangePickerWrapper>
  );
};
